import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyPlans, useSystems } from '@/hooks/use-plans';
import { PlanManagementModal } from './PlanManagementModal';
import { ConsoleSyncBadge } from './ConsoleSyncBadge';
import { useConsoleSyncCustomer } from '@/hooks/useConsoleSyncCustomer';
import { 
  Building2, 
  Users, 
  Package, 
  BarChart3, 
  Mail, 
  Calendar,
  Loader2,
  RefreshCw,
  UserPlus,
  Shield,
  CheckCircle,
  AlertCircle,
  XCircle,
  MoreHorizontal,
  Plus,
  Settings,
  MapPin,
  Phone,
  CreditCard,
  Cloud
} from 'lucide-react';
import { useMpSubscriptions } from '@/hooks/useMpSubscriptions';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DialogFooter } from '@/components/ui/dialog';

interface Company {
  id: string;
  name: string;
  slug: string;
  doc_cnpj?: string;
  timezone: string;
  status: string;
  created_at: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  telefone?: string;
  email?: string;
  branding?: {
    logo?: string;
    primary_color?: string;
    secondary_color?: string;
  };
  console_customer_id?: string | null;
  console_synced_at?: string | null;
}

interface CompanyDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave?: (data: any) => Promise<void>;
}

const timezones = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
  { value: 'America/New_York', label: 'New York (UTC-5)' },
  { value: 'Europe/London', label: 'London (UTC+0)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
];

const statusConfig = {
  active: { label: 'Ativo', variant: 'default', icon: CheckCircle, color: 'text-green-600' },
  trial: { label: 'Trial', variant: 'secondary', icon: AlertCircle, color: 'text-yellow-600' },
  suspended: { label: 'Suspenso', variant: 'destructive', icon: XCircle, color: 'text-red-600' },
};

export function CompanyDetailModal({ open, onOpenChange, company, onSave }: CompanyDetailModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<{ code: string; name: string; currentPlanId?: string } | null>(null);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(false);
  const [inviteAdminModalOpen, setInviteAdminModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    fullName: '',
  });
  const [isInviting, setIsInviting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Console sync hook
  const { syncCompanyToConsole, canSync, getSyncValidationMessage } = useConsoleSyncCustomer();
  
  const [formData, setFormData] = useState({
    name: '',
    doc_cnpj: '',
    timezone: 'America/Sao_Paulo',
    status: 'active',
    endereco_logradouro: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_estado: '',
    endereco_cep: '',
    telefone: '',
    email: '',
    branding: {
      logo: '',
      primary_color: '#2563eb',
      secondary_color: '#64748b',
    },
  });

  // Reset form when company changes
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        doc_cnpj: company.doc_cnpj || '',
        timezone: company.timezone || 'America/Sao_Paulo',
        status: company.status || 'active',
        endereco_logradouro: company.endereco_logradouro || '',
        endereco_numero: company.endereco_numero || '',
        endereco_complemento: company.endereco_complemento || '',
        endereco_bairro: company.endereco_bairro || '',
        endereco_cidade: company.endereco_cidade || '',
        endereco_estado: company.endereco_estado || '',
        endereco_cep: company.endereco_cep || '',
        telefone: company.telefone || '',
        email: company.email || '',
        branding: {
          logo: company.branding?.logo || '',
          primary_color: company.branding?.primary_color || '#2563eb',
          secondary_color: company.branding?.secondary_color || '#64748b',
        },
      });
    }
  }, [company]);

  // Fetch company administrators
  const { data: administrators, isLoading: loadingAdmins, refetch: refetchAdmins } = useQuery({
    queryKey: ['company-administrators', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      
      const { data, error } = await supabase
        .from('user_companies')
        .select(`
          user_id,
          role,
          created_at
        `)
        .eq('company_id', company.id)
        .in('role', ['company_admin', 'admin_company']);
      
      if (error) throw error;
      
      // Get profiles separately
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .in('id', data?.map(item => item.user_id) || []);
      
      if (profilesError) throw profilesError;
      
      // Get user emails from auth.users (this requires proper permissions)
      const userIds = data?.map(item => item.user_id) || [];
      
      return data?.map(item => {
        const profile = profiles?.find(p => p.id === item.user_id);
        return {
          ...item,
          profile: profile || null,
          email: `user-${item.user_id.slice(0, 8)}@company.com` // Placeholder since we can't access auth.users
        };
      }) || [];
    },
    enabled: !!company?.id && open,
  });

  // Fetch admin emails using JOIN approach to avoid PGRST200 error
  const { data: adminEmails, isLoading: loadingAdminEmails, refetch: refetchAdminEmails } = useQuery({
    queryKey: ['admin-emails', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase.rpc('get_admin_emails', { target_company_id: company.id });
      if (error) {
        console.error('Error fetching admin emails via RPC:', error);
        return [];
      }
      return data || [];
    },
    enabled: !!company?.id && open,
  });

  // Fetch company plans 
  const { data: companyPlans, isLoading: loadingPlans, refetch: refetchPlans } = useQuery({
    queryKey: ['company-plans-detail', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      
      console.log('Fetching company plans for:', company.id);
      const { data, error } = await supabase
        .from('company_system_plans')
        .select(`
          id,
          plan_id,
          system_id,
          status,
          start_date,
          end_date,
          billing_period,
          created_at,
          updated_at,
          plans!inner (
            id,
            name,
            description,
            price_monthly,
            price_yearly,
            features
          ),
          systems!inner (
            id,
            code,
            name
          )
        `)
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching company plans:', error);
        throw error;
      }

      console.log('Raw company plans data:', data);
      return data || [];
    },
    enabled: !!company?.id && open,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });


  // Fetch all systems
  const { data: systems = [] } = useSystems();

  // Fetch company subscriptions
  const { data: subscriptions = [], refetch: refetchSubscriptions } = useMpSubscriptions(company?.id);

  // Fetch company statistics
  const { data: stats, isLoading: loadingStats, refetch: refetchStats } = useQuery({
    queryKey: ['company-stats', company?.id],
    queryFn: async () => {
      if (!company?.id) return null;
      
      // Get user count
      const { count: userCount } = await supabase
        .from('user_companies')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', company.id);

      // Get active plans count
      const { count: planCount } = await supabase
        .from('company_system_plans')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', company.id)
        .eq('status', 'active');

      return {
        userCount: userCount || 0,
        planCount: planCount || 0,
        createdAt: company.created_at,
      };
    },
    enabled: !!company?.id && open,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!company?.id) throw new Error('Company ID is required');
      
      const { data: result, error } = await supabase
        .from('companies')
        .update(data)
        .eq('id', company.id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      toast({
        title: 'Sucesso',
        description: 'Empresa atualizada com sucesso',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar empresa',
        variant: 'destructive',
      });
    },
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateMutation.mutateAsync(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refetchAdmins(),
        refetchAdminEmails(),
        refetchPlans(),
        refetchStats(),
        refetchSubscriptions()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const updateFormData = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateBranding = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      branding: { ...prev.branding, [key]: value }
    }));
  };

  const handlePlanManagement = (systemCode: string, systemName: string, currentPlanId?: string) => {
    setSelectedSystem({ code: systemCode, name: systemName, currentPlanId });
    setPlanModalOpen(true);
  };

  const handlePlanSuccess = () => {
    // Refresh plans data
    refetchPlans();
    // Close modal
    setPlanModalOpen(false);
    setSelectedSystem(null);
  };

  const handleInviteAdmin = async () => {
    if (!company?.id || !inviteForm.email || !inviteForm.fullName) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha email e nome completo",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-company-user', {
        body: {
          email: inviteForm.email,
          fullName: inviteForm.fullName,
          role: 'company_admin',
          companyId: company.id,
          systemIds: []
        }
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Erro ao convidar administrador');
      }

      toast({
        title: "Convite enviado!",
        description: `Convite de administrador enviado para ${inviteForm.email}`,
      });

      setInviteForm({ email: '', fullName: '' });
      setInviteAdminModalOpen(false);
      
      refetchAdmins();
      refetchAdminEmails();
    } catch (error: any) {
      toast({
        title: "Erro ao enviar convite",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Handle Console sync
  const handleConsoleSync = async () => {
    if (!company) return;
    
    setIsSyncing(true);
    try {
      const result = await syncCompanyToConsole(company as any);
      
      if (result.success) {
        toast({
          title: 'Sincronizado!',
          description: 'Empresa sincronizada com Console PortalONE',
        });
        // Refresh company data
        queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
        handleRefresh();
      } else {
        toast({
          title: 'Erro na sincronização',
          description: result.error || 'Erro desconhecido',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSyncing(false);
    }
  };

  if (!company) return null;

  const statusInfo = statusConfig[company.status as keyof typeof statusConfig] || statusConfig.active;
  const StatusIcon = statusInfo.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <DialogTitle className="text-xl flex items-center gap-2">
                  {company.name}
                  <ConsoleSyncBadge 
                    syncedAt={company.console_synced_at} 
                    customerId={company.console_customer_id}
                    compact
                  />
                </DialogTitle>
                <DialogDescription className="flex items-center space-x-2">
                  <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                  <span>{statusInfo.label}</span>
                  {company.doc_cnpj && (
                    <>
                      <span>•</span>
                      <span>{company.doc_cnpj}</span>
                    </>
                  )}
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleConsoleSync}
                disabled={isSyncing || !canSync(company as any)}
                title={getSyncValidationMessage(company as any) || 'Sincronizar com Console'}
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Cloud className="h-4 w-4 mr-2" />
                )}
                Sincronizar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>Geral</span>
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Administradores</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center space-x-2">
              <Package className="h-4 w-4" />
              <span>Planos</span>
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Estatísticas</span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 flex-1 overflow-y-auto">
            {/* General Tab */}
            <TabsContent value="general" className="space-y-6">
              <div className="grid gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Informações Básicas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                     <div className="grid grid-cols-2 gap-4">
                       <div>
                         <Label htmlFor="name">Nome da Empresa</Label>
                         <Input
                           id="name"
                           value={formData.name}
                           onChange={(e) => updateFormData('name', e.target.value)}
                         />
                       </div>
                       <div>
                         <Label htmlFor="doc_cnpj">CNPJ</Label>
                         <Input
                           id="doc_cnpj"
                           value={formData.doc_cnpj}
                           onChange={(e) => updateFormData('doc_cnpj', e.target.value)}
                           placeholder="00.000.000/0000-00"
                         />
                       </div>
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                       <div>
                         <Label htmlFor="email">Email da Empresa</Label>
                         <Input
                           id="email"
                           type="email"
                           value={formData.email}
                           onChange={(e) => updateFormData('email', e.target.value)}
                           placeholder="contato@empresa.com"
                         />
                       </div>
                       <div>
                         <Label htmlFor="telefone">Telefone</Label>
                         <Input
                           id="telefone"
                           value={formData.telefone}
                           onChange={(e) => updateFormData('telefone', e.target.value)}
                           placeholder="(11) 99999-9999"
                         />
                       </div>
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                       <div>
                         <Label htmlFor="timezone">Fuso Horário</Label>
                         <Select value={formData.timezone} onValueChange={(value) => updateFormData('timezone', value)}>
                           <SelectTrigger>
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent>
                             {timezones.map((tz) => (
                               <SelectItem key={tz.value} value={tz.value}>
                                 {tz.label}
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>
                       </div>
                       <div>
                         <Label htmlFor="status">Status</Label>
                         <Select value={formData.status} onValueChange={(value) => updateFormData('status', value)}>
                           <SelectTrigger>
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="active">Ativo</SelectItem>
                             <SelectItem value="trial">Trial</SelectItem>
                             <SelectItem value="suspended">Suspenso</SelectItem>
                           </SelectContent>
                         </Select>
                       </div>
                   </div>
                 </CardContent>
               </Card>

               <Card>
                 <CardHeader>
                   <CardTitle className="flex items-center gap-2">
                     <MapPin className="h-4 w-4" />
                     Endereço
                   </CardTitle>
                 </CardHeader>
                 <CardContent className="space-y-4">
                   <div className="grid grid-cols-4 gap-4">
                     <div className="col-span-3">
                       <Label htmlFor="endereco_logradouro">Logradouro</Label>
                       <Input
                         id="endereco_logradouro"
                         value={formData.endereco_logradouro}
                         onChange={(e) => updateFormData('endereco_logradouro', e.target.value)}
                         placeholder="Rua, Avenida, etc."
                       />
                     </div>
                     <div>
                       <Label htmlFor="endereco_numero">Número</Label>
                       <Input
                         id="endereco_numero"
                         value={formData.endereco_numero}
                         onChange={(e) => updateFormData('endereco_numero', e.target.value)}
                         placeholder="123"
                       />
                     </div>
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <Label htmlFor="endereco_complemento">Complemento</Label>
                       <Input
                         id="endereco_complemento"
                         value={formData.endereco_complemento}
                         onChange={(e) => updateFormData('endereco_complemento', e.target.value)}
                         placeholder="Apto, Sala, etc."
                       />
                     </div>
                     <div>
                       <Label htmlFor="endereco_bairro">Bairro</Label>
                       <Input
                         id="endereco_bairro"
                         value={formData.endereco_bairro}
                         onChange={(e) => updateFormData('endereco_bairro', e.target.value)}
                         placeholder="Centro"
                       />
                     </div>
                   </div>
                   <div className="grid grid-cols-3 gap-4">
                     <div>
                       <Label htmlFor="endereco_cidade">Cidade</Label>
                       <Input
                         id="endereco_cidade"
                         value={formData.endereco_cidade}
                         onChange={(e) => updateFormData('endereco_cidade', e.target.value)}
                         placeholder="São Paulo"
                       />
                     </div>
                     <div>
                       <Label htmlFor="endereco_estado">Estado</Label>
                       <Input
                         id="endereco_estado"
                         value={formData.endereco_estado}
                         onChange={(e) => updateFormData('endereco_estado', e.target.value)}
                         placeholder="SP"
                       />
                     </div>
                     <div>
                       <Label htmlFor="endereco_cep">CEP</Label>
                       <Input
                         id="endereco_cep"
                         value={formData.endereco_cep}
                         onChange={(e) => updateFormData('endereco_cep', e.target.value)}
                         placeholder="00000-000"
                       />
                     </div>
                   </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Branding</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="logo">Logo URL</Label>
                      <Input
                        id="logo"
                        value={formData.branding.logo}
                        onChange={(e) => updateBranding('logo', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="primary_color">Cor Primária</Label>
                        <Input
                          id="primary_color"
                          type="color"
                          value={formData.branding.primary_color}
                          onChange={(e) => updateBranding('primary_color', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="secondary_color">Cor Secundária</Label>
                        <Input
                          id="secondary_color"
                          type="color"
                          value={formData.branding.secondary_color}
                          onChange={(e) => updateBranding('secondary_color', e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Salvar Alterações
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Administrators Tab */}
            <TabsContent value="admins" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Administradores</h3>
                  <p className="text-sm text-muted-foreground">
                    Usuários com permissões administrativas nesta empresa
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setInviteAdminModalOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Convidar Admin
                </Button>
              </div>

              {loadingAdminEmails ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-4 p-4 border rounded-lg animate-pulse">
                      <div className="h-10 w-10 bg-muted rounded-full"></div>
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-muted rounded w-1/3"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {adminEmails?.map((admin) => (
                    <Card key={admin.user_id}>
                      <CardContent className="flex items-center justify-between p-4">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Shield className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {(admin as any).full_name || 'Nome não informado'}
                            </p>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span>{(admin as any).email || '—'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">Administrador</Badge>
                          <Button size="sm" variant="ghost">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {adminEmails?.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum administrador encontrado</p>
                      <p className="text-xs mt-1">Esta empresa não possui administradores cadastrados</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Plans Tab */}
            <TabsContent value="plans" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Gestão de Planos</h3>
                  <p className="text-sm text-muted-foreground">
                    Planos ativos e sistemas disponíveis para esta empresa
                  </p>
                </div>
              </div>

              {/* Systems Overview */}
              <div className="space-y-4">
                <h4 className="font-medium">Sistemas Disponíveis</h4>
                <div className="grid gap-4">
                  {systems.map((system) => {
                    const activePlan = companyPlans?.find(cp => (cp.systems as any)?.code === system.code);
                    
                    return (
                      <Card key={system.id} className="border-l-4 border-l-primary/20">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-3">
                                <div className="h-8 w-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                  <Package className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <h5 className="font-medium">{system.name}</h5>
                                  <p className="text-sm text-muted-foreground">
                                    Código: {system.code}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-4">
                                {activePlan ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Plano: {(activePlan.plans as any)?.name}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Sem Plano
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant={activePlan ? "outline" : "default"}
                                onClick={() => handlePlanManagement(
                                  system.code, 
                                  system.name, 
                                  activePlan ? (activePlan.plans as any)?.id : undefined
                                )}
                              >
                                {activePlan ? (
                                  <>
                                    <Settings className="h-4 w-4 mr-1" />
                                    Gerenciar
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4 mr-1" />
                                    Contratar Plano
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Active Plans Section */}
              <div className="space-y-4">
                <h4 className="font-medium">Planos Ativos</h4>
                {loadingPlans ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="p-4 border rounded-lg animate-pulse">
                        <div className="space-y-2">
                          <div className="h-4 bg-muted rounded w-1/4"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {companyPlans?.map((companyPlan) => {
                      const plan = companyPlan.plans as any;
                      const system = companyPlan.systems as any;
                      
                      return (
                        <Card key={companyPlan.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center space-x-2">
                                  <Package className="h-4 w-4 text-primary" />
                                  <span className="font-medium">
                                    {plan?.name || 'Plano'}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {system?.name || 'Sistema'}
                                  </Badge>
                                </div>
                                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                  <span>
                                    Início: {new Date(companyPlan.start_date).toLocaleDateString('pt-BR')}
                                  </span>
                                  <span>
                                    Período: {companyPlan.billing_period === 'monthly' ? 'Mensal' : 'Anual'}
                                  </span>
                                  <span>
                                    Preço: R$ {companyPlan.billing_period === 'monthly' ? plan?.price_monthly : plan?.price_yearly}
                                  </span>
                                </div>
                              </div>
                              <Badge 
                                variant={companyPlan.status === 'active' ? 'default' : 'secondary'}
                              >
                                {companyPlan.status === 'active' ? 'Ativo' : companyPlan.status}
                              </Badge>
                            </div>
                            
                            {plan?.description && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-muted-foreground mb-2">Descrição:</p>
                                <p className="text-sm">{plan.description}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                    
                     {companyPlans?.length === 0 && (
                      <div className="text-center py-6 text-muted-foreground">
                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhum plano contratado</p>
                        <p className="text-xs mt-1">Use os botões acima para contratar planos</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </TabsContent>

            {/* Statistics Tab */}
            <TabsContent value="stats" className="space-y-6">
              <div>
                <h3 className="text-lg font-medium">Estatísticas</h3>
                <p className="text-sm text-muted-foreground">
                  Resumo executivo e métricas da empresa
                </p>
              </div>

              {loadingStats ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i}>
                      <CardContent className="p-6 animate-pulse">
                        <div className="space-y-2">
                          <div className="h-4 bg-muted rounded w-1/2"></div>
                          <div className="h-8 bg-muted rounded w-1/3"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-muted-foreground">Total de Usuários</p>
                          <p className="text-2xl font-bold">{stats?.userCount || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-muted-foreground">Planos Ativos</p>
                          <p className="text-2xl font-bold">{stats?.planCount || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-sm text-muted-foreground">Criada em</p>
                          <p className="text-lg font-medium">
                            {stats?.createdAt ? new Date(stats.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Informações da Empresa</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">ID da Empresa</p>
                      <p className="font-mono text-xs bg-muted p-2 rounded">{company.id}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Slug</p>
                      <p className="font-medium">{company.slug}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Fuso Horário</p>
                      <p className="font-medium">{company.timezone}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <div className="flex items-center space-x-2">
                        <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                        <span className="font-medium">{statusInfo.label}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Plan Management Modal */}
        {selectedSystem && (
          <PlanManagementModal
            open={planModalOpen}
            onOpenChange={setPlanModalOpen}
            companyId={company.id}
            systemCode={selectedSystem.code}
            systemName={selectedSystem.name}
            currentPlanId={selectedSystem.currentPlanId}
            onSuccess={handlePlanSuccess}
          />
        )}
      </DialogContent>

      {/* Modal de Convite de Admin */}
      <Dialog open={inviteAdminModalOpen} onOpenChange={setInviteAdminModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar Administrador</DialogTitle>
            <DialogDescription>
              Convide um novo administrador para {company?.name}. Ele receberá um email com as credenciais de acesso.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="admin-name">Nome Completo *</Label>
              <Input
                id="admin-name"
                value={inviteForm.fullName}
                onChange={(e) => setInviteForm(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="João da Silva"
              />
            </div>
            
            <div>
              <Label htmlFor="admin-email">Email *</Label>
              <Input
                id="admin-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="joao@empresa.com"
              />
            </div>
            
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Este usuário terá permissões de administrador e acesso completo aos recursos da empresa.
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setInviteAdminModalOpen(false)}
              disabled={isInviting}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleInviteAdmin}
              disabled={isInviting || !inviteForm.email || !inviteForm.fullName}
            >
              {isInviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enviar Convite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}